export type DeliveredType = "D" | "S" | "ALL";

export type QuoteMode = "single" | "batch";

export type PackageDimensions = {
  weight: number;
  height: number;
  width: number;
  length: number;
};

export type RateForm = {
  postalCodeOrigin: string;
  postalCodeDestination: string;
  deliveredType: DeliveredType;
  weight: string;
  height: string;
  width: string;
  length: string;
};

export type BatchForm = {
  postalCodeOrigin: string;
  postalCodeDestinations: string;
  deliveredType: DeliveredType;
  packages: string;
};

export type Carrier = "correo" | "andreani";

export const CARRIER_LABEL: Record<Carrier, string> = {
  correo: "Correo Argentino",
  andreani: "Andreani",
};

// Una tarifa sin `carrier` es anterior a Andreani: era de Correo.
export const carrierDe = (rate: { carrier?: Carrier | string }): Carrier =>
  rate.carrier === "andreani" ? "andreani" : "correo";

export type RateResult = {
  carrier?: Carrier;
  deliveredType: "D" | "S" | string;
  productType?: string;
  service: string;
  price: number | string;
  sla: string;
  deliveryTimeMin?: string | number;
  deliveryTimeMax?: string | number;
  raw?: unknown;
};

export type QuoteHistoryItem = RateForm & {
  id: string;
  createdAt: string;
  mode: QuoteMode;
  results: RateResult[];
  error?: string;
  label?: string;
  // Los calcula y los guarda el servidor: de ellos depende un precio, así que
  // el navegador no puede ser la fuente de verdad. Opcionales porque una
  // cotización guardada antes de la fase 2 no los tiene.
  validTo?: string | null;
  volumeCm3?: number;
  aforoKg?: number;
  billableKg?: number;
  customerId?: string;
  sinTarifas?: boolean;
  // Qué transportista falló en una cotización que igual trajo tarifas del otro.
  carrierErrors?: Partial<Record<Carrier, string>>;
};

export type ParsedPackage = PackageDimensions & {
  label?: string;
  volumeCm3: number;
  aforoKg: number;
};
/* =========================
   Órdenes (fases 3-6)
   ========================= */

export type OrderStatus =
  | "cotizada"
  | "pago_pendiente"
  | "pagada"
  | "pago_rechazado"
  | "enviando"
  | "enviada"
  | "envio_fallido"
  | "cancelada";

export type OrderAddress = {
  street: string;
  number: string;
  floor?: string;
  apartment?: string;
  city: string;
  province: string;
  postalCode: string;
};

export type OrderPerson = {
  name: string;
  phone: string;
  email: string;
  // Lo exige Andreani (DNI, o CUIT/CUIL).
  document?: { type: "DNI" | "CUIT" | "CUIL"; number: string };
  address?: OrderAddress;
};

export type OrderPayment = {
  id: string;
  mpPaymentId?: string;
  status: "iniciado" | "aprobado" | "rechazado" | "pendiente" | "cancelado";
  statusDetail?: string;
  amount: number;
  currency: string;
  initPoint?: string;
  createdAt: string;
};

export type OrderEvent = {
  id: string;
  from?: string;
  to: string;
  reason?: string;
  actor: string;
  createdAt: string;
};

export type OrderParcel = {
  id: string;
  weight: number;
  height: number;
  width: number;
  length: number;
  billableKg: number;
  trackingNumber?: string;
};

export type OrderDetail = {
  id: string;
  status: OrderStatus;
  statusReason?: string;
  nextStates: string[];
  isFinal: boolean;
  quoteId: string;
  carrier?: Carrier;
  productType: string;
  deliveredType: "D" | "S";
  price: { amount: number; currency: string; quotedAt?: string | null };
  declaredValue: number;
  postalCodeOrigin: string;
  postalCodeDestination: string;
  sender: OrderPerson;
  recipient: OrderPerson;
  agencyId?: string;
  paymentId?: string;
  trackingNumber?: string;
  createdAt: string;
  updatedAt: string;
  parcels: OrderParcel[];
  events: OrderEvent[];
  payments: OrderPayment[];
};

export type OrderSummary = Pick<
  OrderDetail,
  | "id"
  | "status"
  | "statusReason"
  | "nextStates"
  | "isFinal"
  | "carrier"
  | "productType"
  | "deliveredType"
  | "price"
  | "postalCodeDestination"
  | "agencyId"
  | "trackingNumber"
  | "createdAt"
  | "updatedAt"
>;

export type Agency = {
  agencyId: string;
  name: string;
  city?: string | null;
  state?: string | null;
  address?: string;
  zipCode?: string | null;
  schedule?: string | null;
};

// La última cotización manual de la sesión: lo que hace falta para generar
// una orden a partir de uno de sus resultados.
export type LastQuote = {
  id: string;
  createdAt: string;
  postalCodeOrigin: string;
  postalCodeDestination: string;
  rates: RateResult[];
  carrierErrors?: Partial<Record<Carrier, string>>;
};
